'use client';

import Image from 'next/image';
import { useState, type ReactNode } from 'react';
import { ArrowRight, ChevronRight } from '@1debit/icons';

import * as m from 'motion/react-m';
import { AnimatePresence } from 'motion/react';
import { MENU_CLOSE_DURATION } from './animations';
import { richTextHasContent } from 'utils/formatters';
import { cn, getColorway, getCustomBackgroundColor } from 'utils/styles';
import { BackgroundColor } from 'types/constants';

import { NavContentLinkProps } from './types';
import ChimeKitIcon from '../shared/LinkWithIcon/ChimeKitIcon';
import NavLink from 'components/shared/NavLink/NavLink';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import NavCarousel from './NavCarousel';
import NavNestedItemsList from './NavNestedItemsList';
import { ContentfulCollection, InternalPage, ResourceProps } from 'libs/contentful/types';
import {
  getFirstNestedSubLink,
  HEADER_NAV_ID_PREFIX,
  navSubMenuEyebrowClassName,
  subLinkHasNestedItems,
  subMenuHasNestedItems,
} from './utils';
import { liveUpdateAttrs } from 'libs/contentful/utils';

export interface HorizontalNavSubMenuProps {
  linkContent: NavContentLinkProps;
  resourcesLabel: string;
  resourcesCollection: ContentfulCollection<ResourceProps>;
  resourcesUrl?: string;
  resourcesInternalPage?: InternalPage;
}

const HorizontalNavSubMenu = ({
  linkContent,
  resourcesLabel,
  resourcesCollection,
  resourcesUrl,
  resourcesInternalPage,
}: HorizontalNavSubMenuProps) => {
  const subMenu = linkContent.subMenu;
  const eyebrow = subMenu?.eyebrow;
  const subMenuItems = subMenu?.sublinksCollection.items;
  const hasNestedLayout = subMenuHasNestedItems(subMenu);
  const firstNestedSubLink = subMenu ? getFirstNestedSubLink(subMenu) : null;
  const [selectedNestedSubLink, setSelectedNestedSubLink] = useState(firstNestedSubLink);
  const activeNestedSubLink = subMenuItems?.includes(selectedNestedSubLink)
    ? selectedNestedSubLink
    : firstNestedSubLink;

  const colorPanelClass = subMenu?.color ? getColorway(subMenu.color) : undefined;
  const colorPanelStyle = subMenu?.color ? getCustomBackgroundColor(subMenu.color) : undefined;

  const titleAttrs = subMenu?.titleOverride
    ? liveUpdateAttrs(subMenu.sys?.id, 'titleOverride')
    : liveUpdateAttrs(linkContent.link.sys?.id, 'label');

  const renderSubLinkIcon = (item: NonNullable<typeof subMenuItems>[number]) => {
    if (!item) return null;
    const showIcon = item.chimeKitIconName != null || item.icon?.url != null;
    if (!showIcon) return null;

    if (item.chimeKitIconName) {
      return <ChimeKitIcon iconName={item.chimeKitIconName} />;
    }

    return item.icon ? <Image src={item.icon.url} alt={item.icon.title} height={24} width={24} loading="lazy" /> : null;
  };

  const renderDefaultSubLink = (item: NonNullable<typeof subMenuItems>[number], i: number) => {
    if (!item?.link) return null;

    const showIcon = item.chimeKitIconName != null || item.icon?.url != null;

    return (
      <div className="relative min-h-6" key={i}>
        <div className="absolute top-0 left-0 pr-4">{showIcon && renderSubLinkIcon(item)}</div>
        <div className="pl-10">
          <NavLink
            href={item.link.url}
            internalPage={item.link.internalPage}
            level={1}
            cleanLinkText={item.link.label}
            className="font-sans text-label font-bold after:absolute after:inset-0">
            {item.link.label}
          </NavLink>
          <p className="font-serif text-label-serif">{item.description}</p>
        </div>
      </div>
    );
  };

  const renderNestedSubLink = (item: NonNullable<typeof subMenuItems>[number], i: number) => {
    if (!item?.link) return null;

    const hasNested = subLinkHasNestedItems(item);
    const isActive = activeNestedSubLink === item;
    const showIcon = item.chimeKitIconName != null || item.icon?.url != null;

    return (
      <div
        key={i}
        className={cn('relative rounded-full px-4 py-1.5', hasNested && isActive && BackgroundColor.LightGreen)}
        onMouseEnter={() => hasNested && setSelectedNestedSubLink(item)}
        onFocus={() => hasNested && setSelectedNestedSubLink(item)}>
        <div className="flex items-center gap-3">
          {showIcon && <div className="shrink-0">{renderSubLinkIcon(item)}</div>}
          <div className="flex-1 min-w-0">
            <NavLink
              href={item.link.url}
              internalPage={item.link.internalPage}
              level={1}
              cleanLinkText={item.link.label}
              className="font-sans text-label-saans font-semibold">
              {item.link.label}
            </NavLink>
          </div>
          {hasNested && <ChevronRight className="shrink-0 text-fern size-(--ck-icon-size)" aria-hidden="true" />}
        </div>
      </div>
    );
  };

  // Full-bleed to the menu edge, so the panel carries its own padding instead of
  // sitting inside the horizontal inset the sublink columns use.
  const renderColorPanel = (widthClass: string, children: ReactNode) => (
    <div
      className={cn('max-w-[435px] px-14 py-14', widthClass, colorPanelClass ?? BackgroundColor.LightGreen)}
      style={colorPanelStyle}
      {...liveUpdateAttrs(subMenu?.sys?.id, 'color')}>
      <div className="flex flex-col">
        {eyebrow && richTextHasContent(eyebrow.json) && (
          <div
            className={navSubMenuEyebrowClassName}
            {...liveUpdateAttrs(subMenu?.sys?.id, 'eyebrow')}
            data-testid="nav-sub-menu-eyebrow">
            <RichTextCopy copy={eyebrow} ySpacing="none" inline />
          </div>
        )}
        <h2 className="font-sans text-heading-lg" {...titleAttrs}>
          {subMenu?.titleOverride ?? linkContent.link.label}
        </h2>
      </div>
      {children}
    </div>
  );

  const renderFeaturedPanel = () =>
    renderColorPanel(
      'flex-3',
      <>
        <p className="font-serif text-label-serif my-2" {...liveUpdateAttrs(subMenu?.sys?.id, 'mainDescription')}>
          {subMenu?.mainDescription}
        </p>
        <NavLink
          href={linkContent.link.url}
          internalPage={linkContent.link.internalPage}
          level={0}
          cleanLinkText={subMenu?.linkLabel}
          className="font-sans text-label font-bold">
          {subMenu?.linkLabel} <ArrowRight className="ml-2" />
        </NavLink>
      </>,
    );

  const renderCarouselPanel = () =>
    renderColorPanel(
      'w-[30%] shrink-0',
      <div className="mt-6">
        <NavCarousel
          resourcesLabel={resourcesLabel}
          resourcesCollection={resourcesCollection}
          resourcesUrl={resourcesUrl}
          resourcesInternalPage={resourcesInternalPage}
        />
      </div>,
    );

  return (
    <AnimatePresence>
      <m.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: MENU_CLOSE_DURATION }}
        style={{ overflow: 'hidden' }}
        className={cn(
          'absolute hidden md:block top-full left-0 w-full z-51 border-t border-pebble overflow-hidden',
          BackgroundColor.White,
        )}>
        {hasNestedLayout ? (
          <div className="flex items-stretch min-h-80">
            {renderCarouselPanel()}
            <div className="flex-1 flex gap-12 px-12 py-10">
              <div className="flex-2 flex flex-col gap-1">
                {subMenuItems?.map((item, i) => renderNestedSubLink(item, i))}
              </div>
              <div className="flex-3">
                {activeNestedSubLink && (
                  <NavNestedItemsList
                    subLink={activeNestedSubLink}
                    idPrefix={HEADER_NAV_ID_PREFIX}
                    heading={activeNestedSubLink.link.label}
                  />
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-stretch">
            {renderFeaturedPanel()}
            <div className="flex-4 px-12 py-12">
              <div className="grid grid-cols-3 gap-6">
                {subMenuItems?.map((item, i) => renderDefaultSubLink(item, i))}
              </div>
            </div>
          </div>
        )}
      </m.div>
    </AnimatePresence>
  );
};

export default HorizontalNavSubMenu;
